import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { logQRScan } from '../api';
import { HERO_SLIDES, DISCOVER } from '../hallsGapData';
import { RoomDiningMark } from '../components/FoodArt';
import { COLOR, FONT, glass, glassRed } from '../theme';
import { hotelsById } from '../hotels';

const hotels = hotelsById();

// Room Dining is the hero action; driver + photo are the secondary services.
// ids/routes are unchanged — clicking still goes to /:svc/:hotelId/:roomNumber.
const FEATURED = {
  id: 'food', emoji: '🛎️', title: 'Room Dining',
  sub: 'Fresh local meals delivered straight to your door',
  tag: 'Guest favourite',
};
const SECONDARY = [
  { id: 'driver', emoji: '🚗', title: 'Driver Service', sub: 'Safe, private rides anytime', accent: '#38BDF8' },
  // Photo & Video runs in our other regions — flagged unavailable here so guests
  // sense we operate beyond Victoria. Rendered disabled (no route into the page).
  { id: 'photography', emoji: '📸', title: 'Photo & Video', sub: 'Not available in Victoria', accent: '#A78BFA', unavailable: true },
];

const RED = COLOR.accent;
const RED_DEEP = COLOR.accentDeep;
const spring = { type: 'spring', stiffness: 180, damping: 20 };
const HERO_MS = 6000;

// Open a place in the guest's map app (works with no API key; opens native app on mobile).
const gmap = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
const amap = (q) => `https://maps.apple.com/?q=${encodeURIComponent(q)}`;

/* Image with a graceful gradient fallback if the photo ever fails to load. */
function Photo({ src, alt, position = 'center', style, fallback }) {
  const [failed, setFailed] = useState(false);
  if (failed || !src) {
    return (
      <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #1b1b20, #0d0d10)' }}>
        {fallback}
      </div>
    );
  }
  return (
    <img
      src={src} alt={alt || ''} loading="lazy" decoding="async"
      onError={() => setFailed(true)}
      style={{ ...style, objectFit: 'cover', objectPosition: position }}
    />
  );
}

export default function Home() {
  const { hotelId, roomNumber } = useParams();
  const navigate = useNavigate();
  const hotel = hotels[hotelId] || { name: 'Grand Hotel', tagline: 'Excellence in hospitality' };
  const servicesRef = useRef(null);

  const [slide, setSlide] = useState(0);
  const [hovered, setHovered] = useState(null);
  const [directionsFor, setDirectionsFor] = useState(null); // spot whose map-app chooser is open

  // Auto-rotate the hero banner.
  useEffect(() => {
    if (HERO_SLIDES.length <= 1) return;
    const id = setTimeout(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), HERO_MS);
    return () => clearTimeout(id);
  }, [slide]);

  // Keep the QR-scan analytics exactly as before.
  useEffect(() => {
    if (!hotelId || !roomNumber) return;
    logQRScan({
      hotelId, roomNumber,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      screen: typeof window !== 'undefined' ? `${window.screen.width}x${window.screen.height}` : null,
      url: typeof window !== 'undefined' ? window.location.href : '',
    });
  }, [hotelId, roomNumber]);

  const goService = useCallback((id) => navigate(`/${id}/${hotelId}/${roomNumber}`), [navigate, hotelId, roomNumber]);
  const scrollToServices = () => servicesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const active = HERO_SLIDES[slide];

  return (
    <div style={S.page}>
      {/* ── Top bar ── */}
      <div style={S.topBar}>
        <div style={S.brand}>
          <motion.span
            animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
            style={S.brandDot}
          />
          <span style={S.brandName}>GapGo</span>
        </div>
        <div style={S.roomPill}>
          <span style={S.pillDot} />
          <span style={S.pillText}>Room {roomNumber}</span>
        </div>
      </div>

      {/* ── Hero banner ── */}
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring }} style={S.heroWrap}>
        <div style={S.heroCard}>
          <div style={S.heroMedia}>
            <AnimatePresence initial={false}>
              <motion.div
                key={active.id}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 1.1, ease: 'easeInOut' }}
                style={S.heroImgLayer}
              >
                <Photo src={active.image} alt={active.alt} position={active.position}
                  style={S.heroImg} fallback={<span style={{ fontSize: '64px' }}>⛰️</span>} />
              </motion.div>
            </AnimatePresence>
            <div style={S.heroScrim} aria-hidden />
          </div>

          <div style={S.heroContent}>
            <div style={S.heroChip}>
              <span style={S.chipStar}>✦</span>
              <span style={S.chipText}>HALLS GAP · GRAMPIANS</span>
            </div>
            <h1 style={S.heroTitle}>Welcome to<br />Halls Gap</h1>
            <p style={S.heroSub}>{hotel.name} — your basecamp in the heart of the Grampians.</p>

            <motion.button whileTap={{ scale: 0.97 }} style={S.heroCta} onClick={() => goService('food')}>
              Order Room Dining
              <span style={S.ctaArrow}>→</span>
            </motion.button>
            <button style={S.heroGhost} onClick={scrollToServices}>Explore all services ↓</button>

            <div style={S.dots}>
              {HERO_SLIDES.map((sl, i) => (
                <button key={sl.id} aria-label={`Slide ${i + 1}`} onClick={() => setSlide(i)}
                  style={{ ...S.dot, width: i === slide ? '22px' : '7px', background: i === slide ? '#fff' : 'rgba(255,255,255,0.4)' }} />
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Discover Halls Gap ── */}
      <div style={S.sectionHead}>
        <p style={S.sectionKicker}>DISCOVER</p>
        <h2 style={S.sectionTitle}>Famous around Halls Gap</h2>
      </div>
      <div style={S.discoverScroll}>
        <div style={S.discoverRow}>
          {DISCOVER.map((d, i) => (
            <motion.div
              key={d.id}
              initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i, ...spring }}
              style={S.discoverCard}
            >
              <div style={S.discoverImgBox}>
                <Photo src={d.image} alt={d.name} style={S.discoverImg}
                  fallback={<span style={{ fontSize: '34px' }}>{d.emoji}</span>} />
                <div style={S.discoverImgScrim} aria-hidden />
                <span style={S.discoverName}>{d.name}</span>
              </div>
              <p style={S.discoverTag}>{d.tag}</p>
              <button style={S.dirBtn} onClick={() => setDirectionsFor(d)}>
                Directions<span style={S.dirArrow}>↗</span>
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── At your service ── */}
      <div ref={servicesRef} style={{ ...S.sectionHead, scrollMarginTop: '16px' }}>
        <p style={S.sectionKicker}>AT YOUR SERVICE</p>
        <h2 style={S.sectionTitle}>How can we help?</h2>
      </div>

      {/* Featured — Room Dining */}
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring }}
        whileTap={{ scale: 0.985 }}
        onClick={() => goService(FEATURED.id)}
        style={S.featCard}
      >
        <div style={S.featIcon}><RoomDiningMark size={30} color={RED} /></div>
        <div style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
          <div style={S.featTagRow}>
            <span style={S.featTag}>{FEATURED.tag}</span>
          </div>
          <div style={S.featTitle}>{FEATURED.title}</div>
          <div style={S.featSub}>{FEATURED.sub}</div>
        </div>
        <span style={S.featArrow}>→</span>
      </motion.div>

      {/* Secondary services */}
      <div style={S.secRow}>
        {SECONDARY.map((svc) => {
          if (svc.unavailable) {
            return (
              <div key={svc.id} style={{ ...S.secCard, ...S.secCardOff }} aria-disabled="true">
                <div style={{ ...S.secIcon, background: 'rgba(255,255,255,0.04)' }}>
                  <span style={{ fontSize: '22px', filter: 'grayscale(1)', opacity: 0.55 }}>{svc.emoji}</span>
                </div>
                <div style={{ ...S.secTitle, color: 'rgba(245,245,247,0.35)' }}>{svc.title}</div>
                <div style={S.secOffTag}>
                  <span style={S.secOffDot} />{svc.sub}
                </div>
              </div>
            );
          }
          const on = hovered === svc.id;
          return (
            <motion.div
              key={svc.id}
              whileTap={{ scale: 0.96 }}
              onHoverStart={() => setHovered(svc.id)} onHoverEnd={() => setHovered(null)}
              onClick={() => goService(svc.id)}
              style={{ ...S.secCard, borderColor: on ? `${svc.accent}66` : COLOR.glassBorder }}
            >
              <div style={{ ...S.secIcon, background: on ? `${svc.accent}1f` : 'rgba(255,255,255,0.06)' }}>
                <span style={{ fontSize: '22px' }}>{svc.emoji}</span>
              </div>
              <div style={{ ...S.secTitle, color: on ? svc.accent : COLOR.text }}>{svc.title}</div>
              <div style={S.secSub}>{svc.sub}</div>
            </motion.div>
          );
        })}
      </div>

      {/* ── Footer ── */}
      <div style={S.footer}>
        <div style={S.footerRule} />
        <p style={S.footerHelp}>
          Need a hand? Call reception:{' '}
          <a href="tel:0353564344" style={S.footerPhone}>03 5356 4344</a>
        </p>
        <div style={S.poweredRow}>
          <span style={S.poweredLabel}>POWERED BY</span>
          <a href="https://www.redspeed.com.au" target="_blank" rel="noopener noreferrer" style={S.poweredLink}>
            Redspeed Technologies
          </a>
        </div>
        {process.env.REACT_APP_VERSION && (
          <p style={{ fontSize: '9px', color: INK_FAINT, marginTop: '8px', letterSpacing: '1px' }}>
            {process.env.REACT_APP_VERSION}
          </p>
        )}
      </div>

      {/* ── Directions chooser (Apple / Google) ── */}
      <AnimatePresence>
        {directionsFor && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={S.dirOverlay} onClick={() => setDirectionsFor(null)}
          >
            <motion.div
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 320 }}
              style={S.dirSheet} onClick={(e) => e.stopPropagation()}
            >
              <div style={S.dirHandle} />
              <p style={S.dirKicker}>DIRECTIONS TO</p>
              <h3 style={S.dirTitle}>{directionsFor.name}</h3>
              <p style={S.dirSub}>Halls Gap · Grampians — choose your map app</p>

              <a href={gmap(directionsFor.map)} target="_blank" rel="noopener noreferrer"
                onClick={() => setDirectionsFor(null)} style={{ ...S.dirOpt, ...S.dirOptG }}>
                <span>Google Maps</span><span style={S.dirOptArrow}>↗</span>
              </a>
              <a href={amap(directionsFor.map)} target="_blank" rel="noopener noreferrer"
                onClick={() => setDirectionsFor(null)} style={{ ...S.dirOpt, ...S.dirOptA }}>
                <span>Apple Maps</span><span style={S.dirOptArrow}>↗</span>
              </a>
              <button style={S.dirCancel} onClick={() => setDirectionsFor(null)}>Cancel</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Styles (RedSpeed dark theme — see ../theme.js) ─────────────────────── */
const INK = COLOR.text;
const INK_MUTED = COLOR.textMuted;
const INK_FAINT = COLOR.textFaint;
const LINE = COLOR.glassBorder;
const S = {
  page: {
    minHeight: '100vh', background: COLOR.bg, maxWidth: '430px', margin: '0 auto',
    fontFamily: FONT,
    position: 'relative', overflow: 'hidden', paddingBottom: '44px', color: INK,
  },
  /* Top bar */
  topBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '18px 18px 10px', position: 'relative', zIndex: 2,
  },
  brand: { display: 'flex', alignItems: 'center', gap: '8px' },
  brandDot: { width: '8px', height: '8px', borderRadius: '50%', background: RED, display: 'inline-block', flexShrink: 0 },
  brandName: { fontSize: '17px', fontWeight: '800', color: INK, letterSpacing: '-0.5px' },
  roomPill: {
    display: 'flex', alignItems: 'center', gap: '7px',
    background: COLOR.chipBg, border: `1px solid ${COLOR.chipBorder}`,
    borderRadius: '40px', padding: '7px 14px',
  },
  pillDot: { width: '6px', height: '6px', borderRadius: '50%', background: '#16A34A', boxShadow: '0 0 7px rgba(22,163,74,0.5)', display: 'inline-block', flexShrink: 0 },
  pillText: { fontSize: '12px', fontWeight: '600', color: COLOR.chipText, letterSpacing: '0.3px' },

  /* Hero (photo card keeps white text over a dark scrim) */
  heroWrap: { padding: '4px 14px 6px', position: 'relative', zIndex: 1 },
  heroCard: {
    position: 'relative', borderRadius: '26px', overflow: 'hidden',
    height: 'min(66vh, 540px)', minHeight: '440px',
    border: `1px solid ${COLOR.glassBorder}`, boxShadow: `0 22px 54px ${COLOR.shadowDeep}`,
  },
  heroMedia: { position: 'absolute', inset: 0 },
  heroImgLayer: { position: 'absolute', inset: 0 },
  heroImg: { position: 'absolute', inset: 0, width: '100%', height: '100%' },
  heroScrim: {
    position: 'absolute', inset: 0,
    background: 'linear-gradient(to top, rgba(8,8,11,0.94) 4%, rgba(8,8,11,0.45) 40%, rgba(8,8,11,0.05) 70%),' +
      'linear-gradient(to right, rgba(8,8,11,0.55) 0%, transparent 60%)',
  },
  heroContent: { position: 'absolute', left: 0, right: 0, bottom: 0, padding: '0 22px 22px', zIndex: 2 },
  heroChip: {
    display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '14px',
    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: '40px', padding: '6px 14px', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
  },
  chipStar: { color: RED, fontSize: '10px' },
  chipText: { fontSize: '10px', letterSpacing: '2.5px', color: 'rgba(255,255,255,0.9)', fontWeight: '700' },
  heroTitle: {
    fontSize: 'clamp(38px, 12vw, 52px)', fontWeight: '800', color: '#fff', margin: '0 0 10px',
    lineHeight: 0.98, letterSpacing: '-1.6px', textTransform: 'uppercase', textShadow: '0 2px 24px rgba(0,0,0,0.6)',
  },
  heroSub: { fontSize: '14px', color: 'rgba(255,255,255,0.82)', margin: '0 0 18px', lineHeight: 1.45, maxWidth: '320px', textShadow: '0 1px 14px rgba(0,0,0,0.6)' },
  heroCta: {
    display: 'inline-flex', alignItems: 'center', gap: '10px', width: '100%', justifyContent: 'center',
    background: `linear-gradient(135deg, ${RED} 0%, ${RED_DEEP} 100%)`, color: '#fff', border: 'none',
    borderRadius: '15px', padding: '16px 22px', fontSize: '16px', fontWeight: '800', cursor: 'pointer',
    fontFamily: 'inherit', boxShadow: '0 10px 28px rgba(0,0,0,0.18)', letterSpacing: '0.2px',
  },
  ctaArrow: { fontSize: '17px', fontWeight: '400' },
  heroGhost: {
    display: 'block', width: '100%', marginTop: '11px', background: 'transparent', border: 'none',
    color: 'rgba(255,255,255,0.72)', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
  },
  dots: { display: 'flex', gap: '7px', justifyContent: 'center', marginTop: '14px' },
  dot: { height: '7px', borderRadius: '9999px', border: 'none', padding: 0, cursor: 'pointer', transition: 'width 0.3s ease, background 0.3s ease' },

  /* Section heads */
  sectionHead: { padding: '26px 20px 12px', position: 'relative', zIndex: 1 },
  sectionKicker: { fontSize: '10px', letterSpacing: '3px', color: RED, fontWeight: '700', margin: '0 0 5px' },
  sectionTitle: { fontSize: '21px', fontWeight: '800', color: INK, margin: 0, letterSpacing: '-0.5px' },

  /* Discover strip */
  discoverScroll: { overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '4px', position: 'relative', zIndex: 1 },
  discoverRow: { display: 'flex', gap: '12px', padding: '0 20px', width: 'max-content' },
  discoverCard: { width: '150px', flexShrink: 0 },
  discoverImgBox: {
    position: 'relative', width: '150px', height: '186px', borderRadius: '18px', overflow: 'hidden',
    border: `1px solid ${COLOR.glassBorder}`, background: COLOR.bgElev,
  },
  discoverImg: { position: 'absolute', inset: 0, width: '100%', height: '100%' },
  discoverImgScrim: { position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.85) 6%, transparent 55%)' },
  discoverName: { position: 'absolute', left: '12px', right: '12px', bottom: '11px', color: '#fff', fontSize: '14px', fontWeight: '800', letterSpacing: '-0.2px', lineHeight: 1.2, textShadow: '0 1px 10px rgba(0,0,0,0.7)' },
  discoverTag: { fontSize: '11.5px', color: INK_MUTED, margin: '9px 2px 8px', lineHeight: 1.35 },
  dirBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', width: '100%',
    background: COLOR.chipBg, border: `1px solid ${COLOR.glassBorder}`, borderRadius: '10px',
    padding: '8px 0', color: INK, fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.2px',
  },
  dirArrow: { fontSize: '12px', opacity: 0.6 },

  /* Directions chooser sheet */
  dirOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 400, display: 'flex', alignItems: 'flex-end' },
  dirSheet: { ...glass({ borderRadius: '26px 26px 0 0' }), width: '100%', maxWidth: '430px', margin: '0 auto', padding: '14px 22px 34px', fontFamily: 'inherit' },
  dirHandle: { width: '38px', height: '4px', background: 'rgba(255,255,255,0.2)', borderRadius: '2px', margin: '0 auto 20px' },
  dirKicker: { fontSize: '10px', letterSpacing: '2.5px', color: RED, fontWeight: '700', margin: '0 0 4px' },
  dirTitle: { fontSize: '22px', fontWeight: '800', color: INK, margin: '0 0 4px', letterSpacing: '-0.4px' },
  dirSub: { fontSize: '13px', color: INK_MUTED, margin: '0 0 20px' },
  dirOpt: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', textDecoration: 'none',
    borderRadius: '15px', padding: '16px 18px', fontSize: '16px', fontWeight: '700', marginBottom: '10px', border: '1px solid',
  },
  dirOptG: { background: 'rgba(66,133,244,0.12)', color: '#7DA6FF', borderColor: 'rgba(66,133,244,0.4)' },
  dirOptA: { background: COLOR.chipBg, color: INK, borderColor: COLOR.glassBorder },
  dirOptArrow: { fontSize: '15px', opacity: 0.6 },
  dirCancel: { width: '100%', background: 'transparent', color: INK_MUTED, border: `1px solid ${COLOR.glassBorder}`, borderRadius: '14px', padding: '13px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', marginTop: '4px' },

  /* Featured service */
  featCard: {
    position: 'relative', overflow: 'hidden', margin: '0 14px', display: 'flex', alignItems: 'center', gap: '15px',
    padding: '18px', cursor: 'pointer',
    ...glassRed({ borderRadius: '22px' }),
  },
  featIcon: {
    width: '58px', height: '58px', borderRadius: '16px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.06)', border: `1px solid ${COLOR.glassBorder}`, position: 'relative', zIndex: 1,
  },
  featTagRow: { marginBottom: '6px' },
  featTag: { fontSize: '9.5px', fontWeight: '800', letterSpacing: '1.4px', color: '#fff', background: RED, borderRadius: '40px', padding: '3px 9px', textTransform: 'uppercase' },
  featTitle: { fontSize: '19px', fontWeight: '800', color: INK, letterSpacing: '-0.4px', marginBottom: '3px' },
  featSub: { fontSize: '12.5px', color: INK_MUTED, lineHeight: 1.35 },
  featArrow: { fontSize: '22px', color: INK_FAINT, flexShrink: 0, position: 'relative', zIndex: 1, fontWeight: '300' },

  /* Secondary services */
  secRow: { display: 'flex', gap: '12px', padding: '12px 14px 0' },
  secCard: {
    flex: 1, padding: '16px', borderRadius: '20px', cursor: 'pointer',
    ...glass(),
    transition: 'border-color 0.25s ease',
  },
  secCardOff: { cursor: 'default', opacity: 0.7, background: 'rgba(255,255,255,0.015)', borderStyle: 'dashed', borderColor: 'rgba(255,255,255,0.12)', boxShadow: 'none' },
  secIcon: { width: '46px', height: '46px', borderRadius: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px', transition: 'background 0.25s ease' },
  secTitle: { fontSize: '15px', fontWeight: '700', letterSpacing: '-0.2px', marginBottom: '3px', transition: 'color 0.25s ease' },
  secSub: { fontSize: '11.5px', color: INK_MUTED, lineHeight: 1.35 },
  secOffTag: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#B45309', fontWeight: '700', lineHeight: 1.3 },
  secOffDot: { width: '5px', height: '5px', borderRadius: '50%', background: '#D97706', flexShrink: 0 },

  /* Footer */
  footer: { padding: '30px 24px 0', textAlign: 'center', position: 'relative', zIndex: 1 },
  footerRule: { height: '1px', background: LINE, marginBottom: '18px' },
  footerHelp: { fontSize: '12px', color: INK_MUTED, marginBottom: '13px', lineHeight: 1.6 },
  footerPhone: { color: INK, textDecoration: 'none', fontWeight: '700', letterSpacing: '0.3px' },
  poweredRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  poweredLabel: { fontSize: '9px', letterSpacing: '3px', color: INK_FAINT, fontWeight: '600' },
  poweredLink: { fontSize: '11px', color: INK, textDecoration: 'none', fontWeight: '700' },
};
